import type { Scadenza } from '../api/tipi-dati.ts';

/**
 * Pulsanti d'azione di una riga.
 *
 * Diretti, non dentro un menu: con tre azioni sole nasconderle dietro i
 * puntini aggiunge un clic a ogni operazione del lavoro quotidiano, e
 * l'ufficio queste tre le usa tutto il giorno.
 *
 * La spiegazione di ciascuna sta nel `title`, quindi compare passandoci
 * sopra col mouse invece di occupare spazio in modo permanente.
 *
 * Le etichette visibili sono corte per stringere la colonna, ma le
 * `aria-label` restano estese e includono soggetto e tipo: in una pagina con
 * venti pulsanti «Completata» identici, chi usa un lettore di schermo deve
 * poter capire a quale riga appartiene quello su cui si trova.
 *
 * ⚠️ Ogni `aria-label` **contiene** la parola scritta sul pulsante
 * («Completata», «Annulla», …). Non è un dettaglio: chi comanda a voce dice
 * quello che legge, e se il nome accessibile non contenesse l'etichetta
 * visibile il comando non troverebbe il pulsante.
 */

const SPIEGAZIONI = {
  modifica:
    'Correggi soggetto, tipo, data, preavviso o note. Solo per le scadenze ancora aperte.',
  completa:
    'Registra che è stata eseguita. Se il tipo è ricorrente, crea da sola la scadenza successiva.',
  annulla:
    'Per quando non è più dovuta: mezzo venduto, dipendente cessato, adempimento abolito. Richiede un motivo e resta nello storico.',
  riapri:
    'Se è stata segnata completata per errore. Cancella anche il rinnovo generato, purché nessuno ci abbia ancora lavorato sopra.',
  elimina:
    'Cancella la riga dal database, con i suoi allegati e le notifiche già inviate. Non si può annullare: è per le scadenze che non dovevano proprio esistere.',
} as const;

/**
 * Il pulsante rosso, quando è l'unico modo per arrivarci.
 *
 * ## Dove sta l'eliminazione, e perché non è sempre qui
 *
 * Regola del progetto: **l'eliminazione definitiva non è mai un pari grado
 * delle azioni di tutti i giorni.** Per le scadenze ancora aperte vive quindi
 * in fondo alla scheda di modifica, oltre «Salva modifiche» — per arrivarci
 * bisogna aprire quella riga e scorrere, che è già una decisione presa.
 *
 * Le scadenze completate e annullate però non hanno una scheda di modifica: non
 * si correggono, si riaprono o si lasciano stare. E sono proprio quelle su cui
 * capita di trovarsi una riga di prova o un doppione da buttare. Per loro il
 * pulsante compare qui, con due accorgimenti che lo tengono distinto: sta dopo
 * una barretta separatrice, e non è mai in fila con «Modifica» — su queste
 * righe «Modifica» non esiste.
 */
function BottoneElimina({ riferimento, onElimina }: { riferimento: string; onElimina: () => void }) {
  return (
    <>
      <span className="separatore-azioni" aria-hidden="true" />
      <button
        type="button"
        className="bottone bottone-pericolo"
        title={SPIEGAZIONI.elimina}
        // ⚠️ Contiene la parola scritta sul pulsante: chi comanda a voce dice
        // «Elimina», e il nome accessibile deve poterlo far trovare.
        aria-label={`Elimina definitivamente: ${riferimento}`}
        onClick={onElimina}
      >
        <span aria-hidden="true">🗑</span> Elimina
      </button>
    </>
  );
}

export function AzioniScadenza({
  scadenza,
  puoEliminare,
  onModifica,
  onCompleta,
  onAnnulla,
  onRiapri,
  onElimina,
}: {
  scadenza: Scadenza;
  /** Solo gli amministratori: per gli altri il pulsante rosso non esiste. */
  puoEliminare: boolean;
  onModifica: () => void;
  onCompleta: () => void;
  onAnnulla: () => void;
  onRiapri: () => void;
  onElimina: () => void;
}) {
  const riferimento = `${scadenza.tipo.nome} di ${scadenza.soggetto.denominazione}`;

  // Su una scadenza annullata non c'è niente da fare: si riparte creandone
  // una nuova. Mostrare pulsanti disabilitati lascerebbe solo perplessi.
  // Resta l'eliminazione, per chi ne ha il permesso.
  if (scadenza.stato === 'ANNULLATA') {
    if (!puoEliminare) return null;
    return (
      <div className="cella-azioni">
        <BottoneElimina riferimento={riferimento} onElimina={onElimina} />
      </div>
    );
  }

  if (scadenza.stato === 'COMPLETATA') {
    return (
      <div className="cella-azioni">
        <button
          type="button"
          className="bottone"
          title={SPIEGAZIONI.riapri}
          aria-label={`Riapri: ${riferimento}`}
          onClick={onRiapri}
        >
          <span aria-hidden="true">↺</span> Riapri
        </button>
        {puoEliminare && <BottoneElimina riferimento={riferimento} onElimina={onElimina} />}
      </div>
    );
  }

  return (
    <div className="cella-azioni">
      <button
        type="button"
        className="bottone"
        title={SPIEGAZIONI.modifica}
        aria-label={`Modifica: ${riferimento}`}
        onClick={onModifica}
      >
        <span aria-hidden="true">✎</span> Modifica
      </button>

      <button
        type="button"
        className="bottone"
        title={SPIEGAZIONI.completa}
        aria-label={`Segna completata: ${riferimento}`}
        onClick={onCompleta}
      >
        <span aria-hidden="true">✓</span> Completata
      </button>

      <button
        type="button"
        className="bottone"
        title={SPIEGAZIONI.annulla}
        aria-label={`Annulla scadenza: ${riferimento}`}
        onClick={onAnnulla}
      >
        <span aria-hidden="true">⊘</span> Annulla
      </button>

      {/* Niente pulsante rosso qui: per una scadenza aperta l'eliminazione sta
          in fondo alla scheda di modifica, lontano da questa fila. */}
    </div>
  );
}
